import React, { useState } from 'react';
import {
  Users,
  BookOpen,
  FileText,
  MessageCircle,
  TrendingUp,
  Award,
  Lightbulb,
  Star,
} from 'lucide-react';
import { FrameworkStoryCard } from '../components/collaboration/FrameworkStoryCard';
import { TeamActivityFeed } from '../components/collaboration/TeamActivityFeed';
import { TeamMemberCard } from '../components/collaboration/TeamMemberCard';
import { MetricCard } from '../components/analytics/MetricCard';
import { InsightCard } from '../components/analytics/InsightCard';
import {
  mockTeamMembers,
  mockFrameworkStories,
  mockTeamResources,
  mockTeamActivity,
  mockTeamInsights,
  mockTeamStats,
} from '../data/mockTeamData';

export const TeamCollaboration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'stories' | 'resources' | 'activity' | 'members'>('stories');

  // Sort team members by contribution (for leaderboard)
  const sortedMembers = [...mockTeamMembers].sort((a, b) => {
    const scoreA = a.frameworksExplored + (a.outcomesContributed * 2);
    const scoreB = b.frameworksExplored + (b.outcomesContributed * 2);
    return scoreB - scoreA;
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold text-charcoal-900 mb-2">Team Collaboration</h1>
          <p className="text-charcoal-600">
            Share knowledge, learn from each other, and grow together as a team
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border border-consulting-slate-blue-200">
          <Users className="h-5 w-5" />
          <span className="font-semibold">{mockTeamStats.totalMembers} members</span>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Team Members"
          value={mockTeamStats.totalMembers}
          subtitle={`${mockTeamStats.activeThisWeek} active this week`}
          icon={<Users className="h-6 w-6 text-consulting-slate-blue-600" />}
          color="primary"
        />
        <MetricCard
          title="Framework Stories"
          value={mockTeamStats.totalStories}
          subtitle="Success stories shared"
          icon={<FileText className="h-6 w-6 text-sage-600" />}
          color="sage"
        />
        <MetricCard
          title="Team Resources"
          value={mockTeamStats.totalResources}
          subtitle="Templates & playbooks"
          icon={<BookOpen className="h-6 w-6 text-[var(--mustard)]" />}
          color="amber"
        />
        <MetricCard
          title="Frameworks Explored"
          value={mockTeamStats.frameworksExplored}
          subtitle="Across all members"
          icon={<TrendingUp className="h-6 w-6 text-coral-600" />}
          color="coral"
        />
      </div>

      {/* Team Insights */}
      {mockTeamInsights.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-6 w-6 text-[var(--mustard)]" />
            <h2 className="text-2xl font-bold text-charcoal-900">Team Insights</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {mockTeamInsights.slice(0, 4).map((insight, idx) => (
              <InsightCard key={idx} insight={{
                type: insight.type as any,
                title: insight.title,
                description: insight.description,
                metric: insight.metric || 0,
                trend: 'up',
                actionable: insight.relatedUrl ? `View details` : undefined,
              }} />
            ))}
          </div>
        </section>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-[var(--rule)]">
        <nav className="flex gap-6">
          {[
            { id: 'stories', label: 'Success Stories', icon: Star, count: mockTeamStats.totalStories },
            { id: 'resources', label: 'Team Resources', icon: BookOpen, count: mockTeamStats.totalResources },
            { id: 'activity', label: 'Activity Feed', icon: MessageCircle },
            { id: 'members', label: 'Team Members', icon: Users, count: mockTeamStats.totalMembers },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`
                flex items-center gap-2 px-1 py-3 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id
                  ? 'border-consulting-slate-blue-600 text-consulting-slate-blue-700'
                  : 'border-transparent text-charcoal-600 hover:text-charcoal-900'
                }
              `}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`
                  px-2 py-0.5 rounded-full text-xs
                  ${activeTab === tab.id
                    ? 'bg-consulting-slate-blue-100 text-consulting-slate-blue-700'
                    : 'bg-[var(--hover-tint)] text-charcoal-600'
                  }
                `}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="animate-fadeIn">
        {activeTab === 'stories' && (
          <section>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-charcoal-900 mb-2">Framework Success Stories</h2>
              <p className="text-charcoal-600">
                Real-world examples of how your team has applied frameworks to solve problems
              </p>
            </div>

            {/* Featured Stories */}
            {mockFrameworkStories.filter(s => s.featured).length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4 flex items-center gap-2">
                  <Star className="h-5 w-5 text-[var(--mustard)] fill-current" />
                  Featured Stories
                </h3>
                <div className="space-y-6">
                  {mockFrameworkStories.filter(s => s.featured).map(story => (
                    <FrameworkStoryCard key={story.id} story={story} />
                  ))}
                </div>
              </div>
            )}

            {/* All Stories */}
            <div>
              <h3 className="text-lg font-semibold text-charcoal-900 mb-4">All Stories</h3>
              <div className="space-y-6">
                {mockFrameworkStories.filter(s => !s.featured).map(story => (
                  <FrameworkStoryCard key={story.id} story={story} />
                ))}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'resources' && (
          <section>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-charcoal-900 mb-2">Team Resources</h2>
              <p className="text-charcoal-600">
                Templates, playbooks, and guides created by your team
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {mockTeamResources.map(resource => (
                <div
                  key={resource.id}
                  className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-charcoal-900 mb-2">{resource.title}</h3>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        resource.type === 'template' ? 'bg-consulting-slate-blue-100 text-consulting-slate-blue-700' :
                        resource.type === 'playbook' ? 'bg-sage-100 text-sage-700' :
                        resource.type === 'guide' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
                        'bg-charcoal-100 text-charcoal-700'
                      }`}>
                        {resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
                      </span>
                    </div>
                  </div>

                  <p className="text-charcoal-700 mb-4">{resource.description}</p>

                  {/* Author & Stats */}
                  <div className="flex items-center justify-between pt-4 border-t border-[var(--rule)]">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center">
                        <Users className="h-3 w-3 text-sage-700" />
                      </div>
                      <span className="text-sm text-charcoal-600">{resource.author.name}</span>
                    </div>
                    <div className="text-sm text-charcoal-600">
                      {resource.downloads} downloads
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {mockTeamResources.length === 0 && (
              <div className="text-center py-12 bg-[var(--surface)] border-2 border-dashed border-[var(--rule)]">
                <BookOpen className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
                <p className="text-charcoal-600">No team resources yet</p>
                <p className="text-sm text-charcoal-500 mt-2">Be the first to share a template or guide!</p>
              </div>
            )}
          </section>
        )}

        {activeTab === 'activity' && (
          <section>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-charcoal-900 mb-2">Team Activity Feed</h2>
              <p className="text-charcoal-600">
                Recent activity from your team members
              </p>
            </div>

            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
              <TeamActivityFeed activities={mockTeamActivity} />
            </div>
          </section>
        )}

        {activeTab === 'members' && (
          <section>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-charcoal-900 mb-2">Team Members</h2>
              <p className="text-charcoal-600">
                Your collaborative learning community
              </p>
            </div>

            {/* Leaderboard */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Award className="h-6 w-6 text-[var(--mustard)]" />
                <h3 className="text-lg font-semibold text-charcoal-900">Top Contributors</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sortedMembers.slice(0, 3).map((member, idx) => (
                  <TeamMemberCard key={member.id} member={member} rank={idx + 1} />
                ))}
              </div>
            </div>

            {/* All Members */}
            <div>
              <h3 className="text-lg font-semibold text-charcoal-900 mb-4">All Members</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sortedMembers.slice(3).map((member) => (
                  <TeamMemberCard key={member.id} member={member} />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Simulated Notice */}
      <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 text-sm">
        <p className="text-[var(--mustard)]">
          <strong>📌 Demo Note:</strong> This is a simulated collaborative environment. In a production system, 
          this would connect to a real backend with user authentication, real-time updates, and persistent storage.
        </p>
      </div>
    </div>
  );
};

